// SPDX-License-Identifier: BUSL-1.1

/**
 * ReconstructionPage — operator reconstruction view (CEO-Sprint-70 E1-S1-T2).
 *
 * Path: /reconstruction?sessionId=... (or /reconstruction?importRunId=...)
 * Auth: sessionAuth via RequireAuth in app.tsx
 *
 * Answers: "What happened in this governed session?"
 *   - Run overview
 *   - Governance timeline (entry per attribution)
 *   - Approval gates (entries with kind=approval_granted)
 *   - Checkpoint/resume events
 *   - Takeover lineage (previousActorId → nextActorId)
 *   - Proposed vs approved vs applied change-set items
 *
 * Authority distinction: governance-authoritative vs imported supporting narrative
 * Evidence gaps: explicit markers, never hidden
 *
 * Reuses the canonical /api/archive/timeline API surfaced by
 * ReconstructionQueryService.  No separate reconstruction data model.
 */

import { useState } from 'react';
import { useState as useStateRaw } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ChevronRight, ChevronDown, Clock, GitBranch, Lightbulb, Shield, User } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useCurrentOrg } from '@/contexts/org-context';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface AttributionEntry {
  timelineId: string;
  kind: string;
  occurredAt: string;
  actorId: string;
  actorType: 'human' | 'agent' | 'system';
  agentProfileId: string | null;
  summary: string;
  takeoverAt: string | null;
  takeoverReason: string | null;
  previousActorId: string | null;
  nextActorId: string | null;
  approvalChainId: string | null;
  governanceContext: string | null;
  interventionCount: number;
  lastHumanIntervention: string | null;
}

interface EvidenceGap {
  type: 'missing_event' | 'unverified_attribution' | 'imported_only_claim' | 'checkpoint_divergence';
  entryId?: string;
  reason: string;
}

interface AttributionTimelineResponse {
  version?: string;
  entries: (AttributionEntry & { authority?: 'authoritative' | 'supporting' | 'unknown' })[];
  nextCursor: string | null;
  evidenceGaps?: EvidenceGap[];
  totalCount: number | null;
}

// ─── Coaching types ─────────────────────────────────────────────────────────────
// Sentinel B-6 mitigation: SessionCoach is duplicated here because the console
// package does not depend on @build-ceo/api-client. This is a known limitation.
// CoachingPriority and CoachingSuggestion are also kept local.
type CoachingPriority = 'critical' | 'high' | 'medium' | 'low';
interface CoachingSuggestion {
  id: string;
  priority: CoachingPriority;
  headline: string;
  detail: string;
  action: string;
  signal?: string;
}
interface SessionCoach {
  version: '1';
  sessionId: string;
  generatedAt: string;
  session: {
    totalEvents: number;
    riskLevel: 'low' | 'medium' | 'high';
    evidenceGaps: number;
    driftRecords: number;
    changeSets: number;
  };
  suggestions: CoachingSuggestion[];
  patterns: string[];
  violations: string[];
  drift?: {
    records: Array<{
      id: string;
      status: string;
      driftType: string;
      policyVersion: string;
      observedPolicyVersion: string;
      detectedAt: string;
      resolutionNote?: string | null;
    }>;
    summary: { open: number; acknowledged: number; resolved: number };
  } | null;
}

const KIND_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  session_started: { label: 'Session start', color: 'text-blue-300', bg: 'bg-blue-900/40' },
  approval_granted: { label: 'Approval', color: 'text-emerald-300', bg: 'bg-emerald-900/40' },
  actor_changed: { label: 'Takeover', color: 'text-amber-300', bg: 'bg-amber-900/40' },
  tool_execution: { label: 'Tool', color: 'text-cyan-300', bg: 'bg-cyan-900/40' },
  checkpoint_written: { label: 'Checkpoint', color: 'text-violet-300', bg: 'bg-violet-900/40' },
  checkpoint_resumed: { label: 'Resume', color: 'text-violet-300', bg: 'bg-violet-900/40' },
};

export function ReconstructionPage(): React.ReactElement {
  const { currentOrg } = useCurrentOrg();
  const slug = currentOrg?.slug ?? '';
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const importRunId = searchParams.get('importRunId');

  const { data, isLoading, error } = useQuery({
    queryKey: ['reconstruction', slug, sessionId, importRunId],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (sessionId) qs.set('sessionId', sessionId);
      if (importRunId) qs.set('importRunId', importRunId);
      qs.set('limit', '200'); // Sessions are bounded
      return apiFetch<AttributionTimelineResponse>(
        `/orgs/${slug}/archive/timeline?${qs.toString()}`,
      );
    },
    enabled: !!slug && (!!sessionId || !!importRunId),
    staleTime: 10_000,
    retry: 1,
  });

  // CEO-Sprint-71 E2-S1-T1: fetch episode audit-trail (forensic evidence chain).
  // Each entry is provenance='imported-narrative-only', trusted=false — the
  // authority boundary is surfaced at the render layer.
  const { data: auditData } = useQuery({
    // C-8 fix (Sprint 75 follow-up): the audit-trail route mounts at
    // /api/agents/:agentId/episodes/:episodeId/audit-trail. The console only
    // has sessionId in scope. Pass ?sessionId= so the route can derive the
    // episode lookup server-side (sessionId → ops_agent_sessions → ops_episodes).
    queryKey: ['audit-trail', sessionId],
    queryFn: () =>
      apiFetch<{ entries: Array<{ kind: string; label: string; actorId: string | null; source: string; trusted: false }>; count: number; hasNext: boolean; nextCursor: string | null }>(
        `/agents/fake/episodes/fake/audit-trail?sessionId=${encodeURIComponent(sessionId!)}`,
      ),
    enabled: !!sessionId,
    staleTime: 30_000,
    retry: 1,
  });

  const auditEntries = auditData?.entries ?? [];

  // CEO-Sprint-72 E1-S1-T3: evidence-package view — aggregate timeline + lineage
  // + gaps into one structured view with export buttons (JSON-LD, CSV).
  const { data: evidencePkgData } = useQuery({
    queryKey: ['evidence-package', slug, sessionId],
    queryFn: () => {
      const qs = new URLSearchParams({ sessionId: sessionId ?? '', limit: '200' });
      return apiFetch<{
        version: string;
        metadata: { organizationId: string; generatedAt: string };
        timeline: { entries: Array<AttributionEntry & { authority: string }>; hasNext: boolean; nextCursor: string | null };
        evidenceGaps: { gaps: Array<{ type: string; entryId?: string; reason: string }>; summary: { total: number } };
        lineage: {
          session?: Array<{
            sessionId: string;
            changeSetId: string;
            importRunId: string | null;
            changeSetStatus: string;
            linkedAt: string;
            title?: string;
            authorId?: string;
            approvedBy?: string | null;
            approvedAt?: string | null;
            appliedAt?: string | null;
            verifiedAt?: string | null;
            rolledBackAt?: string | null;
            riskLevel?: string;
          }>;
          changeSetEvents?: Array<{
            eventId: string;
            changeSetId: string;
            eventType: string;
            actorId: string;
            occurredAt: string;
          }>;
        };
      }>(`/orgs/${slug}/archive/evidence-package?${qs.toString()}`);
    },
    enabled: !!slug && !!sessionId,
    staleTime: 30_000,
    retry: 1,
  });

  const entries = data?.entries ?? [];
  const gaps = data?.evidenceGaps ?? [];

  // ── Coaching panel — CEO-Sprint-78 E2-S1-T4 ─────────────────────────────────
  const [coachExpanded, setCoachExpanded] = useStateRaw(false);

  const { data: coachData, isLoading: coachLoading } = useQuery({
    queryKey: ['session-coach', slug, sessionId],
    queryFn: () =>
      apiFetch<SessionCoach>(
        `/orgs/${slug}/archive/coach/${encodeURIComponent(sessionId ?? '')}`,
      ),
    enabled: !!slug && !!sessionId && coachExpanded,
    staleTime: 30_000,  // Sentinel C-1: shorter to avoid stale coaching after drift acknowledge
    retry: 1,
  });

  // Derive overview metrics
  const overview = deriveOverview(entries);
  const approvals = entries.filter((e) => e.kind === 'approval_granted');
  const checkpoints = entries.filter((e) => e.kind === 'checkpoint_written' || e.kind === 'checkpoint_resumed');
  const takeovers = entries.filter((e) => e.takeoverAt);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-100">
          <Shield className="size-6 text-emerald-400" />
          Operator Reconstruction
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          {sessionId
            ? `Session: ${sessionId}`
            : importRunId
              ? `Import run: ${importRunId}`
              : 'Provide a sessionId or importRunId query parameter'}
        </p>
      </div>

      {/* Missing parameter warning */}
      {!sessionId && !importRunId && (
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm text-neutral-300">
                No reconstruction target specified. Add <code>?sessionId=...</code> or
                {' '}<code>?importRunId=...</code> to the URL.
              </p>
              <p className="mt-2 text-xs text-neutral-500">
                Reconstructions are computed from the archive timeline API.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evidence gaps — surfaced explicitly */}
      {gaps.length > 0 && (
        <Card className="border-amber-700/50 bg-amber-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="size-4" />
              Evidence gaps ({gaps.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {gaps.map((gap, i) => (
              <div key={i} className="rounded border border-amber-800/40 bg-amber-900/20 p-3">
                <p className="text-xs font-mono text-amber-400">{gap.type}</p>
                <p className="mt-1 text-sm text-neutral-300">{gap.reason}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Run overview */}
      {entries.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatCard icon={User} label="Operators" value={overview.uniqueOperators} />
          <StatCard icon={GitBranch} label="Takeovers" value={takeovers.length} />
          <StatCard icon={Shield} label="Approvals" value={approvals.length} />
          <StatCard icon={Clock} label="Checkpoints" value={checkpoints.length} />
        </div>
      )}

      {/* Loading / error / empty / loaded states */}
      {isLoading && (
        <Card>
          <CardContent className="space-y-3 py-6">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-red-700/50 bg-red-950/20">
          <CardContent className="py-6">
            <p className="text-sm text-red-300">Failed to load reconstruction.</p>
            <p className="mt-1 text-xs text-red-400">{(error as Error).message}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && entries.length === 0 && sessionId && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <Clock className="size-8 text-neutral-700" />
            <p className="text-sm text-neutral-400">No archive entries for this session.</p>
            <p className="text-xs text-neutral-500">
              The session may not have been ingested into the archive yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Governance timeline */}
      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Governance Timeline</CardTitle>
            <p className="text-xs text-neutral-500">
              All events are filtered to local-archivist source. Authority labels indicate
              governance-authoritative vs imported supporting narrative.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-neutral-800">
              {entries.map((entry) => (
                <ReconstructionEventRow key={entry.timelineId} entry={entry} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Approval gates section */}
      {approvals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Approval Gates ({approvals.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-0">
            {approvals.map((approval) => (
              <div
                key={approval.timelineId}
                className="flex items-center gap-3 border-b border-neutral-800 px-6 py-3 last:border-b-0"
              >
                <Badge className="bg-emerald-900/40 text-emerald-300">Approved</Badge>
                <span className="text-sm text-neutral-300">{approval.actorId}</span>
                <span className="text-xs text-neutral-500">
                  {relativeTime(approval.occurredAt)}
                </span>
                {approval.approvalChainId && (
                  <Link
                    to={`/change-sets/${approval.approvalChainId}`}
                    className="ml-auto text-xs text-blue-400 hover:underline"
                  >
                    Chain: {approval.approvalChainId}
                  </Link>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Episode audit trail (CEO-Sprint-71 E2-S1-T1)
          --  forensic evidence chain imported from archivist.
          Every entry carries provenance='imported-narrative-only' and
          trusted=false; the operator UI must visually label them as
          supporting evidence only, NEVER as authoritative. */}
      {auditEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Episode Audit Trail ({auditEntries.length})
            </CardTitle>
            <p className="text-xs text-neutral-500">
              Forensic evidence chain (imported narrative — UNTRUSTED; supporting evidence only).
            </p>
          </CardHeader>
          <CardContent className="space-y-2 p-0">
            {auditEntries.map((entry, i) => (
              <div
                key={i}
                className="flex items-start gap-3 border-b border-neutral-800 px-6 py-3 last:border-b-0"
              >
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-700/50 text-amber-400"
                  title="provenance=imported-narrative-only; trusted=false"
                >
                  UNTRUSTED
                </Badge>
                <span className="text-xs font-mono text-neutral-400">
                  {entry.kind}
                </span>
                <span className="text-sm text-neutral-300">{entry.label}</span>
                {entry.actorId && (
                  <span className="ml-auto text-xs text-neutral-500">
                    {entry.actorId}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Evidence Package section — CEO-Sprint-72 E1-S1-T3
          Aggregates timeline + lineage + evidence gaps with export buttons.
          Only shown when sessionId is present (same guard as audit-trail section). */}
      {!!sessionId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-4" />
              Evidence Package
              {evidencePkgData?.timeline?.entries?.length != null && (
                <span className="text-sm font-normal text-neutral-500">
                  ({evidencePkgData.timeline.entries.length} entries)
                </span>
              )}
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <a
                href={`/orgs/${slug}/archive/evidence-package?sessionId=${encodeURIComponent(sessionId ?? '')}&limit=200`}
                download="evidence-package.json"
                className="rounded border border-blue-700 bg-blue-950 px-3 py-1 text-xs text-blue-300 hover:border-blue-500"
              >
                JSON
              </a>
              <a
                href={`/orgs/${slug}/archive/evidence-package?sessionId=${encodeURIComponent(sessionId ?? '')}&limit=200&format=jsonld`}
                download="evidence-package.jsonld"
                className="rounded border border-purple-700 bg-purple-950 px-3 py-1 text-xs text-purple-300 hover:border-purple-500"
              >
                JSON-LD
              </a>
              <a
                href={`/orgs/${slug}/archive/evidence-package?sessionId=${encodeURIComponent(sessionId ?? '')}&limit=200&format=csv`}
                download="evidence-package.csv"
                className="rounded border border-emerald-700 bg-emerald-950 px-3 py-1 text-xs text-emerald-300 hover:border-emerald-500"
              >
                CSV
              </a>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Evidence gaps warning banner */}
            {((evidencePkgData?.evidenceGaps?.gaps?.length ?? 0) > 0) && (
              <div className="rounded border border-amber-700/50 bg-amber-950/50 p-3">
                <div className="mb-1 flex items-center gap-2 text-sm font-medium text-amber-400">
                  <AlertTriangle className="size-4" />
                  Evidence Gaps ({evidencePkgData?.evidenceGaps?.summary?.total ?? 0})
                </div>
                <ul className="space-y-1">
                  {evidencePkgData?.evidenceGaps?.gaps?.slice(0, 5).map((gap, i) => (
                    <li key={i} className="text-xs text-amber-300/70">
                      <span className="font-mono text-amber-400">{gap.type}</span>: {gap.reason}
                    </li>
                  ))}
                  {(evidencePkgData?.evidenceGaps?.gaps?.length ?? 0) > 5 && (
                    <li className="text-xs text-neutral-500">
                      +{(evidencePkgData?.evidenceGaps?.gaps?.length ?? 0) - 5} more gaps
                    </li>
                  )}
                </ul>
              </div>
            )}
            {/* Timeline entries with authority badges */}
            {evidencePkgData?.timeline?.entries?.length === 0 && (
              <p className="py-4 text-center text-sm text-neutral-500">
                No timeline entries in evidence package.
              </p>
            )}
            <ul className="divide-y divide-neutral-800">
              {evidencePkgData?.timeline?.entries?.slice(0, 20).map((entry) => (
                <li key={entry.timelineId} className="flex items-center gap-3 px-2 py-2">
                  <Badge
                    className={`shrink-0 text-xs ${
                      entry.authority === 'authoritative'
                        ? 'border-emerald-700/50 bg-emerald-900/30 text-emerald-400'
                        : entry.authority === 'supporting'
                          ? 'border-amber-700/50 bg-amber-900/30 text-amber-400'
                          : 'border-neutral-700 bg-neutral-900 text-neutral-500'
                    }`}
                    title={`authority: ${entry.authority}`}
                  >
                    {entry.authority}
                  </Badge>
                  <span className="truncate text-sm text-neutral-300">{entry.summary}</span>
                  <span className="ml-auto shrink-0 text-xs text-neutral-500">
                    {relativeTime(entry.occurredAt)}
                  </span>
                </li>
              ))}
            </ul>
            {(evidencePkgData?.timeline?.hasNext) && (
              <p className="py-2 text-center text-xs text-neutral-500">
                More entries available — use pagination cursors.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Coaching panel — CEO-Sprint-78 E2-S1-T4 ──────────────────────────────────
          AI-native coaching report with prioritized suggestions, patterns,
          violations, and drift summary. Loads lazily on click. */}
      {!!sessionId && (
        <Card>
          <CardHeader>
            <button
              className="flex w-full cursor-pointer items-center gap-2 text-left"
              onClick={() => setCoachExpanded((v) => !v)}
            >
              <Lightbulb className="size-4 shrink-0 text-blue-400" />
              <CardTitle className="flex-1">AI Coaching</CardTitle>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 text-neutral-500 transition-transform',
                  coachExpanded && 'rotate-180',
                )}
              />
            </button>
            <CardDescription>
              Prioritized improvement suggestions based on session analysis
            </CardDescription>
          </CardHeader>

          {!coachExpanded && (
            <CardContent className="pt-0">
              <button
                onClick={() => setCoachExpanded(true)}
                className="rounded border border-blue-800 bg-blue-950 px-3 py-1.5 text-xs text-blue-300 hover:border-blue-600"
              >
                Load coaching report
              </button>
            </CardContent>
          )}

          {coachExpanded && (
            <CardContent className="space-y-4 pt-0">
              {/* Loading */}
              {coachLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              )}

              {/* Error */}
              {!coachLoading && !coachData && (
                <p className="text-sm text-red-400">
                  Failed to load coaching report.
                </p>
              )}

              {/* Report */}
              {!coachLoading && coachData && (
                <>
                  {/* Session summary */}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-400">
                    <span>
                      Events:{' '}
                      <span className="text-neutral-200">{coachData.session.totalEvents}</span>
                    </span>
                    <span
                      className={cn(
                        'font-medium',
                        coachData.session.riskLevel === 'high' && 'text-red-400',
                        coachData.session.riskLevel === 'medium' && 'text-amber-400',
                        coachData.session.riskLevel === 'low' && 'text-emerald-400',
                      )}
                    >
                      Risk: {coachData.session.riskLevel}
                    </span>
                    <span>
                      Gaps:{' '}
                      <span className="text-neutral-200">{coachData.session.evidenceGaps}</span>
                    </span>
                    <span>
                      Drift:{' '}
                      <span className="text-neutral-200">{coachData.session.driftRecords}</span>
                    </span>
                    <span>
                      Change-sets:{' '}
                      <span className="text-neutral-200">{coachData.session.changeSets}</span>
                    </span>
                  </div>

                  {/* Drift summary */}
                  {coachData.drift && (
                    <div className="flex gap-4 text-xs text-neutral-500">
                      <span className="text-red-400">
                        {coachData.drift.summary.open} open
                      </span>
                      <span className="text-amber-400">
                        {coachData.drift.summary.acknowledged} acknowledged
                      </span>
                      <span className="text-emerald-400">
                        {coachData.drift.summary.resolved} resolved
                      </span>
                    </div>
                  )}

                  {/* Patterns */}
                  {coachData.patterns.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs font-medium text-neutral-500 uppercase tracking-wide">
                        Patterns detected
                      </p>
                      <ul className="space-y-1">
                        {coachData.patterns.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-neutral-400">
                            <span className="mt-1 size-1.5 shrink-0 rounded-full bg-blue-500" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Violations */}
                  {coachData.violations.length > 0 && (
                    <div className="rounded border border-red-800 bg-red-950 p-3">
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-red-400">
                        Policy violations
                      </p>
                      <ul className="space-y-1">
                        {coachData.violations.map((v, i) => (
                          <li key={i} className="text-sm text-red-300">
                            ⚠ {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Suggestions */}
                  {coachData.suggestions.length === 0 ? (
                    <p className="py-2 text-sm text-neutral-500">
                      No coaching suggestions for this session.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {coachData.suggestions.map((s) => {
                        const priorityColor: Record<string, string> = {
                          critical: 'border-red-700 bg-red-950',
                          high: 'border-amber-700 bg-amber-950',
                          medium: 'border-blue-700 bg-blue-950',
                          low: 'border-neutral-700 bg-neutral-950',
                        };
                        const textColor: Record<string, string> = {
                          critical: 'text-red-400',
                          high: 'text-amber-400',
                          medium: 'text-blue-400',
                          low: 'text-neutral-400',
                        };
                        return (
                          <div
                            key={s.id}
                            className={cn(
                              'rounded border p-3',
                              priorityColor[s.priority] ?? 'border-neutral-700 bg-neutral-950',
                            )}
                          >
                            <div className="mb-1 flex items-center gap-2">
                              <Badge
                                className={cn(
                                  'text-xs',
                                  textColor[s.priority] ?? 'text-neutral-400',
                                  (s.priority === 'critical' || s.priority === 'high') &&
                                    'border-red-700/50 bg-red-900/30',
                                  s.priority === 'medium' && 'border-blue-700/50 bg-blue-900/30',
                                  s.priority === 'low' && 'border-neutral-700 bg-neutral-900',
                                )}
                              >
                                {s.priority}
                              </Badge>
                              <span className={cn('text-sm font-medium', textColor[s.priority])}>
                                {s.headline}
                              </span>
                            </div>
                            <p className="mb-2 text-xs text-neutral-400">{s.detail}</p>
                            <p className="text-xs text-neutral-300">
                              <span className="text-neutral-500">→</span> {s.action}
                            </p>
                            {s.signal && (
                              <p className="mt-1 font-mono text-xs text-neutral-600">
                                Signal: {s.signal}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="text-xs text-neutral-600">
                    Generated: {new Date(coachData.generatedAt).toLocaleString()}
                  </p>
                </>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Lineage section — CEO-Sprint-72 E2-S1-T2
          Shows session→change-set→event chain from the evidence-package response. */}
      {(evidencePkgData?.lineage?.session?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="size-4" />
              Cross-Surface Lineage
              <span className="text-sm font-normal text-neutral-500">
                ({evidencePkgData?.lineage?.session?.length ?? 0} change set(s))
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {evidencePkgData?.lineage?.session?.map((cs) => (
              <div
                key={cs.changeSetId}
                className="rounded border border-neutral-800 bg-neutral-950 p-3"
              >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <Badge
                    className={`text-xs ${
                      cs.changeSetStatus === 'verified'
                        ? 'border-emerald-700/50 bg-emerald-900/30 text-emerald-400'
                        : cs.changeSetStatus === 'applied'
                          ? 'border-blue-700/50 bg-blue-900/30 text-blue-400'
                          : cs.changeSetStatus === 'approved'
                            ? 'border-purple-700/50 bg-purple-900/30 text-purple-400'
                            : cs.changeSetStatus === 'rejected' || cs.changeSetStatus === 'failed'
                              ? 'border-red-700/50 bg-red-900/30 text-red-400'
                              : 'border-neutral-700 bg-neutral-900 text-neutral-400'
                    }`}
                    title={`Status: ${cs.changeSetStatus}`}
                  >
                    {cs.changeSetStatus}
                  </Badge>
                  {cs.riskLevel && (
                    <Badge variant="outline" className="text-xs">
                      {cs.riskLevel}
                    </Badge>
                  )}
                  {cs.title && (
                    <span className="text-sm text-neutral-300">{cs.title}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-neutral-500">
                  {cs.authorId && <span>Author: <span className="text-neutral-400">{cs.authorId.slice(0, 12)}</span></span>}
                  {cs.approvedBy && <span>Approved: <span className="text-neutral-400">{cs.approvedBy.slice(0, 12)}</span></span>}
                  {cs.appliedAt && <span>Applied: <span className="text-neutral-400">{cs.appliedAt.slice(0, 10)}</span></span>}
                  {cs.verifiedAt && <span>Verified: <span className="text-neutral-400">{cs.verifiedAt.slice(0, 10)}</span></span>}
                </div>
                <div className="mt-1 text-xs text-neutral-600">
                  CS: {cs.changeSetId.slice(0, 12)} · linked {relativeTime(cs.linkedAt)}
                </div>
              </div>
            ))}
            {(evidencePkgData?.lineage?.changeSetEvents?.length ?? 0) > 0 && (
              <div className="border-t border-neutral-800 pt-3">
                <p className="mb-2 text-xs font-medium text-neutral-500">
                  {evidencePkgData?.lineage?.changeSetEvents?.length} event(s)
                </p>
                <ul className="space-y-1">
                  {evidencePkgData?.lineage?.changeSetEvents?.slice(0, 10).map((e) => (
                    <li key={e.eventId} className="flex items-center gap-2 text-xs text-neutral-400">
                      <ChevronRight className="size-3 shrink-0" />
                      <span className="font-mono text-neutral-500">{e.eventType}</span>
                      <span className="text-neutral-600">by {e.actorId.slice(0, 12)}</span>
                      <span className="ml-auto text-neutral-600">{relativeTime(e.occurredAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Takeover lineage */}
      {takeovers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Takeover Lineage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-0">
            {takeovers.map((takeover) => (
              <div
                key={takeover.timelineId}
                className="flex items-center gap-3 border-b border-neutral-800 px-6 py-3 last:border-b-0"
              >
                <GitBranch className="size-4 shrink-0 text-amber-400" />
                <span className="font-mono text-sm text-neutral-300">
                  {takeover.previousActorId ?? 'unknown'} → {takeover.nextActorId ?? 'unknown'}
                </span>
                <span className="text-xs text-neutral-500">
                  {relativeTime(takeover.occurredAt)}
                </span>
                {takeover.takeoverReason && (
                  <p className="ml-auto max-w-md truncate text-xs text-neutral-500">
                    {takeover.takeoverReason}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReconstructionEventRow({
  entry,
}: {
  entry: AttributionEntry & { authority?: 'authoritative' | 'supporting' | 'unknown' };
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const config = KIND_LABELS[entry.kind] ?? {
    label: entry.kind,
    color: 'text-neutral-300',
    bg: 'bg-neutral-800',
  };
  const authority = entry.authority ?? 'unknown';

  return (
    <li>
      <button
        className="flex w-full items-start gap-3 px-6 py-4 text-left transition hover:bg-neutral-900/50"
        onClick={() => setExpanded((e) => !e)}
      >
        <time className="w-14 shrink-0 pt-0.5 font-mono text-xs text-neutral-500">
          {relativeTime(entry.occurredAt)}
        </time>

        <span className={cn('mt-0.5 shrink-0 rounded px-2 py-0.5 text-xs font-medium', config.bg, config.color)}>
          {config.label}
        </span>

        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs text-neutral-400">
          {entry.actorType === 'human' ? 'H' : entry.actorType === 'agent' ? 'A' : 'S'}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-300">{entry.summary}</p>
          <p className="mt-0.5 text-xs text-neutral-600">
            {entry.actorId}
            {entry.agentProfileId && ` · profile ${entry.agentProfileId}`}
          </p>
        </div>

        <Badge
          variant="outline"
          className={cn(
            'shrink-0 text-xs',
            authority === 'authoritative' && 'border-emerald-700/50 text-emerald-400',
            authority === 'supporting' && 'border-amber-700/50 text-amber-400',
            authority === 'unknown' && 'border-neutral-700 text-neutral-500',
          )}
        >
          {authority}
        </Badge>

        <ChevronRight
          className={cn(
            'mt-1 size-4 shrink-0 text-neutral-600 transition-transform',
            expanded && 'rotate-90',
          )}
        />
      </button>

      {expanded && (
        <div className="mx-6 mb-4 rounded border border-neutral-800 bg-neutral-950 p-3">
          <pre className="overflow-x-auto text-xs text-neutral-400">
            {JSON.stringify(entry, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <Icon className="size-5 shrink-0 text-neutral-500" />
        <div>
          <p className="text-xs text-neutral-500">{label}</p>
          <p className="text-2xl font-bold text-neutral-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function deriveOverview(entries: AttributionEntry[]): {
  uniqueOperators: number;
  takeoverCount: number;
  approvalCount: number;
  checkpointCount: number;
} {
  const actors = new Set(entries.map((e) => e.actorId));
  return {
    uniqueOperators: actors.size,
    takeoverCount: entries.filter((e) => e.takeoverAt).length,
    approvalCount: entries.filter((e) => e.kind === 'approval_granted').length,
    checkpointCount: entries.filter(
      (e) => e.kind === 'checkpoint_written' || e.kind === 'checkpoint_resumed',
    ).length,
  };
}
